import React from 'react';

// MUI Components
import { Card, Box, Typography, IconButton, Tooltip } from '@mui/material';

// Components
import Loader from '../../General/Loader';
import OlcButton from '~/components/General/OlcButton';

// Icons
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as ResetZoomIcon,
  MailOutlined as MailIcon,
} from '@mui/icons-material';
import {
  Add3CIcon,
  AddCIcon,
  EnvelopeCIcon,
  ResetZoomCIcon,
  ZoomInCIcon,
  ZoomOutCIcon,
} from '~/assets/images';

// Styles
import './styles.scss';

const WorkflowPanel = ({
  steps,
  zoomLevel,
  handleZoomIn,
  handleZoomOut,
  handleResetZoom,
  StepCard,
  handleAddAction,
  handleEditStep,
  handleDeleteStep,
  handleDownloadMailerProof,
  downloadingProof,
  handleDownloadEnvelopeProof,
  downloadingEnvelope,
  setEditingStep,
  setInsertIndex,
  setOpenDelayDialog,
  setOpenActionDialog,
  loadingCampaign,
}) => {
  return (
    <Card
      className="workflowPanelCard"
      sx={{
        position: 'relative',
      }}
    >
      {/* Zoom Controls */}
      <Box className="zoomControlsWrapper">
        <Typography variant="body2" sx={{ color: '#303030', fontFamily: 'Inter', mr: 1 }}>
          Zoom: {Math.round(zoomLevel * 100)}%
        </Typography>
        <Tooltip title="Reset Zoom">
          <IconButton
            disableRipple
            onClick={handleResetZoom}
            sx={{
              color: '#ed5c2f',
              width: 32,
              height: 32,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <img src={ResetZoomCIcon} alt="Reset Zoom" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom In">
          <IconButton
            disableRipple
            onClick={handleZoomIn}
            disabled={zoomLevel >= 2}
            sx={{
              color: '#ed5c2f',
              width: 32,
              height: 32,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <img src={ZoomInCIcon} alt="Zoom In" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom Out">
          <IconButton
            disableRipple
            onClick={handleZoomOut}
            disabled={zoomLevel <= 0.3}
            sx={{
              color: '#ed5c2f',
              width: 32,
              height: 32,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <img src={ZoomOutCIcon} alt="Zoom Out" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        className="workflowContentWrapper"
        sx={{
          // Grid background pattern
          backgroundImage: `
            linear-gradient(rgba(237, 92, 47, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(237, 92, 47, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '20px 20px',
          backgroundColor: '#fff7ea',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'var(--olc-scrollbar-track)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'var(--olc-scrollbar-thumb)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'var(--olc-scrollbar-thumb-hover)',
            },
          },
          '@media (width <= 1200px) and (width >= 900px)': {
            padding: '0 10px',
          },
        }}
      >
        {/* Workflow Content with Zoom */}
        <Box
          className="workflowContent"
          sx={{
            transform: `scale(${zoomLevel})`,
            display: loadingCampaign ? 'flex' : 'block',
            justifyContent: loadingCampaign ? 'center' : 'flex-start',
            alignItems: loadingCampaign ? 'center' : 'flex-start',
            paddingLeft: zoomLevel > 1 ? '100px' : '0px',
            '@media (width <= 900px)': {
              paddingLeft: zoomLevel > 1 ? '200px' : '0px',
            },
          }}
        >
          {/* Steps Section */}
          {loadingCampaign ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Loader />
            </Box>
          ) : steps.length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {steps.map((step, index) => (
                <StepCard
                  key={step.id}
                  step={step}
                  index={index}
                  steps={steps}
                  handleEditStep={handleEditStep}
                  handleDeleteStep={handleDeleteStep}
                  handleDownloadMailerProof={handleDownloadMailerProof}
                  downloadingProof={downloadingProof}
                  handleDownloadEnvelopeProof={handleDownloadEnvelopeProof}
                  downloadingEnvelope={downloadingEnvelope}
                  setEditingStep={setEditingStep}
                  setInsertIndex={setInsertIndex}
                  setOpenDelayDialog={setOpenDelayDialog}
                  setOpenActionDialog={setOpenActionDialog}
                />
              ))}
            </Box>
          ) : (
            <Box className="noActionsState">
              <Box className="noActionsStateIcon">
                <img src={EnvelopeCIcon} alt="Envelope" />
              </Box>
              <Typography className="noActionsStateTitle">
                Start Building Your Mail Campaign
              </Typography>
              <Typography className="noActionsStateDescription">
                Create a campaign that alternates between Actions and Delays
              </Typography>
              <OlcButton
                variant="primary"
                startIcon={<img src={Add3CIcon} alt="Add" />}
                onClick={() => handleAddAction(0)}
              >
                Add First Action
              </OlcButton>
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default WorkflowPanel;
